import { EditOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import { Input, List, Switch, Modal, Col, Row, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { cloneDeep, get, indexOf, remove } from "lodash";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Button from "../../../../components/functional_components/Button/PFButton";
import { checkRoles, showError, showSuccess, trimData } from "../../../../utils/utilities";
import ProjectEstimate from "../../../../services/api/ProjectEstimate";
import { projectEstimateTabs } from "../../../../utils/properties";
import WorkPackageApi from "../../../../services/api/WorkPackage";
import Wbs from "../../../../services/api/Wbs";
import { setLoading } from "../../../../redux/ducks/Loading";
import { useTranslation } from "react-i18next";

const { confirm } = Modal;

export default function WorkPackage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [state, setState] = useState({
    wbsList: [],
    wbsListCheckedNew: [],
    wbsListCheckedEdit: [],
    wbsCategoryList: {},
    loading: false,
    wpModal: {
      visible: false,
      type: "",
      data: [],
    },
    isEdit: false,
  });

  const userInfo = useSelector((state) => get(state.userInfoDuck, "userInfo", {}));

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      isEdit: checkRoles(userInfo.roles, projectEstimateTabs["preventiveForm"].edit),
    }));
    workPackageList();
    getWbsStructure();
  }, []);

  const workPackageList = async () => {
    dispatch(setLoading(true));
    const workPackage = await ProjectEstimate.projectEstimateWorkPackage(params.id, "allPages=true");
    if (!workPackage.error) {
      setState((prevState) => ({
        ...prevState,
        wbsList: get(workPackage, "data", []),
      }));
    } else {
      showError(get(workPackage, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const closeWpModal = () => {
    formikWP.resetForm();
    setState((prevState) => ({
      ...prevState,
      wbsListCheckedEdit: [],
      wpModal: { visible: false, type: "", data: [] },
    }));
  };

  const getWbsStructure = async () => {
    setState((prevState) => ({ ...prevState, loading: true }));
    const wbsStructure = await Wbs.getWbsStructureList("allPages=true&active=true");
    if (!wbsStructure.error && wbsStructure.data) {
      let structure = {};
      wbsStructure.data.forEach((wbs) => {
        if (!structure[wbs.category.id]) {
          structure[wbs.category.id] = {
            name: wbs.category.name,
            wbsList: [],
          };
        }
        structure[wbs.category.id].wbsList.push(wbs);
        setState((prevState) => ({ ...prevState, wbsCategoryList: structure }));
      });
    } else {
      showError(get(wbsStructure, "error.messageCode", "ER000"));
    }
    setState((prevState) => ({ ...prevState, loading: false }));
  };

  const handleWpNew = () => {
    setState((prevState) => ({
      ...prevState,
      wbsListCheckedEdit: cloneDeep(state.wbsListCheckedNew),
      wpModal: { visible: true, type: "new", data: [] },
    }));
  };

  const handleWpEdit = (wpId) => async () => {
    dispatch(setLoading(true));
    const wbsCategories = await WorkPackageApi.projectEstimateWorkPackageDetails(wpId);
    if (!wbsCategories.error && wbsCategories.data) {
      const disabledWbs = [];
      formikWP.setFieldValue("id", wpId);
      formikWP.setFieldValue("name", wbsCategories.data.name);
      wbsCategories.data.wbs.map((w) => w.blocked && disabledWbs.push(w.id));
      wbsCategories.data.disabledWbs = disabledWbs;
      setState((prevState) => ({
        ...prevState,
        wbsListCheckedEdit: wbsCategories.data.wbsIds,
        wpModal: {
          visible: true,
          type: "edit",
          data: wbsCategories.data,
        },
      }));
    } else {
      showError(get(wbsCategories, "error.messageCode", "ER000"));
    }
    dispatch(setLoading(false));
  };

  const deleteCallback = (wpId) => async () => {
    confirm({
      title: t("general.ensureDelete"),
      icon: <ExclamationCircleOutlined />,
      onOk: async () => {
        dispatch(setLoading(true));
        const response = await WorkPackageApi.projectEstimateWorkPackageDelete(wpId);
        if (!response.error) {
          showSuccess();
          workPackageList();
        } else {
          showError(get(response, "error.messageCode", "ER000"));
        }
        dispatch(setLoading(false));
      },
      onCancel() {},
    });
  };

  const formikWP = useFormik({
    initialValues: {
      id: "",
      projectEstimateId: params.id,
      name: "",
    },
    validate: (values) => {
      values = trimData(values);
      let errors = {};
      if (!values.name) {
        errors.name = "Required!";
      }
      return errors;
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      let dataSave = values;
      dataSave.wbsIds = state.wbsListCheckedEdit;
      setState((prevState) => ({ ...prevState, loading: true }));
      const response = await WorkPackageApi.projectEstimateWorkPackageSave(dataSave);
      if (!response.error && response.data) {
        showSuccess();
        closeWpModal();
        workPackageList();
      } else {
        showError(get(response, "error.messageCode", "ER000"));
      }
      setState((prevState) => ({ ...prevState, loading: false }));
    },
  });

  const changeSwitch = (check, id) => {
    let wbsListCheckedEdit = [];
    wbsListCheckedEdit = cloneDeep(state.wbsListCheckedEdit);
    if (check) {
      wbsListCheckedEdit.push(id);
    } else {
      remove(wbsListCheckedEdit, (i) => i === id);
    }

    setState((prevState) => ({ ...prevState, wbsListCheckedEdit }));
  };

  return (
    <>
      <Row justify="end" className="mb-1em">
        <Button
          isDisabled={!state.isEdit}
          currentIcon={<PlusOutlined className="icon-medium" />}
          clickCallback={handleWpNew}
        />
      </Row>
      <List
        header={<strong>{t("projectEstimate.detail.tabs.workPackage.title")}</strong>}
        dataSource={state.wbsList}
        size="small"
        renderItem={(item) => (
          <List.Item
            actions={[
              <Row className={`list-actions ${!state.isEdit && "disabled"}`} key={item.id}>
                <Col onClick={state.isEdit ? handleWpEdit(item.id) : undefined}>
                  <EditOutlined className="icon-small mr-10" />
                </Col>
                {!item.deletable ? (
                  <Col>
                    {" "}
                    <DeleteOutlined className="icon-small disabled no-pointer" />{" "}
                  </Col>
                ) : (
                  <Col onClick={deleteCallback(item.id)}>
                    {" "}
                    <DeleteOutlined className="icon-small" />
                  </Col>
                )}
              </Row>,
            ]}
          >
            <List.Item.Meta title={<div>{`WP${item.counter} | ${item.name} | ${item.status}`}</div>} />
          </List.Item>
        )}
      />

      <Modal
        destroyOnHidden
        width={800}
        title={
          <b>
            {state.wpModal.type === "new"
              ? t("projectEstimate.detail.tabs.workPackage.newWp")
              : state.wpModal.data.name}
          </b>
        }
        open={state.wpModal.visible}
        onCancel={closeWpModal}
        footer={[
          <Button
            isLoading={state.loading}
            key="okButton"
            currentIcon={<SaveOutlined className="icon-medium" />}
            clickCallback={formikWP.submitForm}
            isDisabled={!(formikWP.dirty && formikWP.isValid)}
          />,
        ]}
      >
        {state.loading ? (
          <div className="container-spinner-positioning">
            <Spin spinning={state.loading} className="spinner-positioning" />
          </div>
        ) : (
          ""
        )}
        <Row>
          <Col span={24}>
            <Row>
              <label className="fw-bold">{t("projectEstimate.detail.tabs.workPackage.description")}*</label>
            </Row>
            <Row>
              <Input
                required
                id="name"
                name="name"
                placeholder={t("projectEstimate.detail.tabs.workPackage.description")}
                onChange={formikWP.handleChange}
                onBlur={formikWP.handleBlur}
                value={formikWP.values.name}
              />
            </Row>
          </Col>
        </Row>
        <Row gutter={8}>
          {Object.keys(state.wbsCategoryList).map((str) => {
            return (
              <Col key={str} lg={8} md={24}>
                <Row className="mb-1em">
                  <label className="fw-bold mt-10">{state.wbsCategoryList[str].name}</label>
                </Row>
                {state.wbsCategoryList[str].wbsList.map((wb) => (
                  <Row key={wb.id} className="mb-1em">
                    <Col span={5}>
                      <Switch
                        defaultChecked={
                          state.wpModal.type === "new"
                            ? indexOf(state.wbsListCheckedNew, wb.id) !== -1
                            : indexOf(state.wbsListCheckedEdit, wb.id) !== -1
                        }
                        disabled={
                          (state.wpModal.data.disabledWbs && indexOf(state.wpModal.data.disabledWbs, wb.id) !== -1) ||
                          (indexOf(state.wpModal.data.wbsIds, wb.id) !== -1 && state.wpModal.data.status === "RELEASED")
                        }
                        onChange={(check) => changeSwitch(check, wb.id)}
                      />
                    </Col>
                    <Col span={19}>
                      <div className="boxValue left">{wb.name}</div>
                    </Col>
                  </Row>
                ))}
              </Col>
            );
          })}
        </Row>
      </Modal>
    </>
  );
}
